import { Component, OnInit, ElementRef, ViewChild, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ViaCEPService, ViaCEPResponse } from '../../core/services/viacep.service';
import { PartnerService } from '../../core/services/partner.service';
import { PartnerInscriptionDraftService } from '../../core/services/partner-inscription-draft.service';
import { ToastService } from '../../shared/services/toast.service';
import { CustomValidators } from '../../shared/validators';
import { I18nService } from '../../core/services/i18n.service';
import { CnpjLookupService, CnpjOfficeData } from '../../core/services/cnpj-lookup.service';
import {
  PartnerInscriptionData,
  PartnerInscriptionProduct,
  PartnerInscriptionSaveRequest,
} from '../../core/models/partner.model';
import { formatZipCode } from '../../core/utils/utils';
import { formatCnpj, unformatCnpj } from '../../core/utils/cnpj.util';
import { CardWrapperComponent } from '../../shared/components/card-wrapper/card-wrapper.component';
import { PartnerInscriptionStepGuard } from '../../core/guards/partner-inscription-step.guard';
import { PartnerInscriptionSuccessGuard } from '../../core/guards/partner-inscription-success.guard';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';

import {
  PartnerInscriptionStep,
  PartnerInscriptionStepperComponent
} from './partner-inscription-stepper.component';
import { CadastroParceirosCnpjComponent } from './cadastro-parceiros-cnpj/cadastro-parceiros-cnpj.component';
import { CnpjValidationResult } from './cadastro-parceiros-cnpj/cadastro-parceiros-cnpj.component';
import { InscriptionStep1Component } from './steps/step1/inscription-step1.component';
import { InscriptionStep2Component } from './steps/step2/inscription-step2.component';
import { InscriptionStep3Component } from './steps/step3/inscription-step3.component';
import { PartnerInscriptionSuccessCtaComponent } from './partner-inscription-success-cta/partner-inscription-success-cta.component';

@Component({
  selector: 'umkt-cadastro-parceiros-form',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    CardWrapperComponent,
    PartnerInscriptionStepperComponent,
    CadastroParceirosCnpjComponent,
    InscriptionStep1Component,
    InscriptionStep2Component,
    InscriptionStep3Component,
    PartnerInscriptionSuccessCtaComponent,
    I18nPipe,
  ],
  templateUrl: './cadastro-parceiros-form.component.html',
  styleUrls: ['./cadastro-parceiros.component.scss', './cadastro-parceiros-form.component.scss'],
})
export class CadastroParceirosFormComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  partnerForm: FormGroup;
  isSubmitting = false;
  isLoadingCEP = false;
  isLoadingCNPJ = false;
  selectedFiles: File[] = [];
  fileNames: string = 'Nenhum arquivo selecionado';
  showSuccessScreen = false;
  termsUrl = 'https://mkt-image-front.s3.us-east-1.amazonaws.com/Contrato+de+Intermedia%C3%A7%C3%A3o+de+Neg%C3%B3cio.pdf';

  wizardStep = -1;
  inscriptionSteps: PartnerInscriptionStep[] = [];
  readonly stepFractionTotal = 3;
  private readonly stepRoutes = ['step-1', 'step-2', 'step-3'] as const;
  private langSubscription?: Subscription;

  private destroy$ = new Subject<void>();

  private readonly step0Keys = [
    'cnpj',
    'razaoSocial',
    'setorAtuacao',
    'nomeResponsavel',
    'telefone',
    'email',
  ] as const;

  private readonly step0LockedKeys = ['cnpj', 'razaoSocial', 'setorAtuacao', 'email'] as const;
  private readonly step1Keys = ['cep', 'endereco', 'numero', 'bairro', 'cidade', 'estado'] as const;

  brazilStates = [
    { value: 'AC', label: 'Acre' },
    { value: 'AL', label: 'Alagoas' },
    { value: 'AP', label: 'Amapá' },
    { value: 'AM', label: 'Amazonas' },
    { value: 'BA', label: 'Bahia' },
    { value: 'CE', label: 'Ceará' },
    { value: 'DF', label: 'Distrito Federal' },
    { value: 'ES', label: 'Espírito Santo' },
    { value: 'GO', label: 'Goiás' },
    { value: 'MA', label: 'Maranhão' },
    { value: 'MT', label: 'Mato Grosso' },
    { value: 'MS', label: 'Mato Grosso do Sul' },
    { value: 'MG', label: 'Minas Gerais' },
    { value: 'PA', label: 'Pará' },
    { value: 'PB', label: 'Paraíba' },
    { value: 'PR', label: 'Paraná' },
    { value: 'PE', label: 'Pernambuco' },
    { value: 'PI', label: 'Piauí' },
    { value: 'RJ', label: 'Rio de Janeiro' },
    { value: 'RN', label: 'Rio Grande do Norte' },
    { value: 'RS', label: 'Rio Grande do Sul' },
    { value: 'RO', label: 'Rondônia' },
    { value: 'RR', label: 'Roraima' },
    { value: 'SC', label: 'Santa Catarina' },
    { value: 'SP', label: 'São Paulo' },
    { value: 'SE', label: 'Sergipe' },
    { value: 'TO', label: 'Tocantins' }
  ];
  private formBuilder = inject(FormBuilder);
  private viaCEPService = inject(ViaCEPService);
  private partnerService = inject(PartnerService);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private i18n = inject(I18nService);
  private cnpjLookupService = inject(CnpjLookupService);
  private partnerInscriptionDraft = inject(PartnerInscriptionDraftService);
  private partnerInscriptionStepGuard = inject(PartnerInscriptionStepGuard);
  private partnerInscriptionSuccessGuard = inject(PartnerInscriptionSuccessGuard);

  constructor() {
    this.partnerForm = this.formBuilder.group({
      cnpj: ['', [Validators.required, CustomValidators.cnpj]],
      razaoSocial: ['', [Validators.required, CustomValidators.noWhitespaceOnly, Validators.maxLength(255)]],
      nomeFantasia: ['', [Validators.maxLength(255)]],
      setorAtuacao: ['', [Validators.required, CustomValidators.noWhitespaceOnly, Validators.maxLength(255)]],
      nomeResponsavel: ['', [Validators.required, CustomValidators.noWhitespaceOnly, Validators.maxLength(255)]],
      telefone: ['', [Validators.required, CustomValidators.phone]],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          CustomValidators.emailNoSpaces,
          CustomValidators.emailWithDomainValidator(),
          Validators.maxLength(255),
        ],
      ],
      cep: ['', [Validators.required, CustomValidators.cep]],
      endereco: ['', [Validators.required, Validators.maxLength(255)]],
      numero: ['', [Validators.required, Validators.maxLength(20)]],
      complemento: ['', [Validators.maxLength(255)]],
      bairro: ['', [Validators.required, Validators.maxLength(100)]],
      cidade: ['', [Validators.required, Validators.maxLength(100)]],
      estado: ['', [Validators.required]],
      descricaoProduto: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(255)]],
      urlProduto: ['', [Validators.maxLength(255)]],
      arquivo: ['', [Validators.required]],
      termosAceitos: [false, [Validators.requiredTrue]],
      startupStage: ['', [Validators.required]],
      businessType: [[], [(ctrl: FormControl) => Array.isArray(ctrl.value) && ctrl.value.length > 0 ? null : { required: true }]],
      revenueModel: ['', [Validators.required]],
      mainSegment: ['', [Validators.required]],
      techVertical: [''],
      operationScale: ['', [Validators.required]],
    });

  }

  ngOnInit(): void {
    this.langSubscription = this.i18n.getCurrentLanguage().subscribe(() => {
      this.loadInscriptionSteps();
    });
    this.loadInscriptionSteps();

    this.route.url.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.syncWizardStepFromRoute();
      this.applyPendingCnpjOfficePrefill();
      this.syncSuccessScreenFromRoute();
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (this.showSuccessScreen) {
        return;
      }

      this.showSuccessScreen = false;

      const urlToken = typeof params['token'] === 'string' ? params['token'].trim() : '';
      if (urlToken) {
        this.partnerInscriptionDraft.setActiveToken(urlToken);
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { token: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }

      const activeToken = this.partnerInscriptionDraft.getActiveToken();
      if (!activeToken) {
        return;
      }
      const stepForRequest = this.getBackendStepForRequest();
      this.loadInscriptionFromToken(activeToken, stepForRequest);
    });

    this.partnerForm.get('cep')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(cep => {
        if (cep && this.viaCEPService.validarCEP(cep)) {
          this.buscarEndereco(cep);
        }
      });

    this.partnerForm.get('cnpj')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((cnpj: string) => {
        const cleanCNPJ = unformatCnpj(cnpj);
        const cnpjCtrl = this.partnerForm.get('cnpj');

        if (cleanCNPJ.length === 14 && cnpjCtrl?.valid) {
          this.fetchCNPJInfo(cleanCNPJ);
        } else if (cleanCNPJ.length === 14 && cnpjCtrl?.errors?.['invalidCNPJ']) {
          this.toastService.showError('CNPJ inválido');
        } else if (cleanCNPJ.length < 14) {
          this.partnerForm.patchValue(
            {
              razaoSocial: '',
              setorAtuacao: '',
              nomeResponsavel: '',
            },
            { emitEvent: false }
          );
        }
      });

    this.tryHydrateFromDraftCache();
  }

  ValidationCnpjfinished(validationResult: CnpjValidationResult): void {
    const normalizedCnpj = unformatCnpj(validationResult.cnpj);
    this.partnerForm.patchValue(
      { cnpj: formatCnpj(normalizedCnpj) },
      { emitEvent: false }
    );

    if (validationResult.partnerData) {
      const targetStep = this.getTargetWizardStepIndexAfterLoad(validationResult.partnerData);
      const integrationToken = validationResult.integrationToken?.trim();
      if (integrationToken) {
        this.partnerInscriptionDraft.setActiveToken(integrationToken);
        this.partnerInscriptionDraft.set(integrationToken, validationResult.partnerData);
        this.patchFormFromBackend(validationResult.partnerData);
        this.applyStep0TokenLock();
        void this.navigateToWizardStep(targetStep, true);
        return;
      }
      this.patchFormFromBackend(validationResult.partnerData);
      this.applyStep0TokenLock();
      void this.navigateToWizardStep(targetStep, true);
      return;
    }

    const goToCompanyStep = () => this.navigateToWizardStep(0);

    const withCnpjForNextScreen = (office: CnpjOfficeData): CnpjOfficeData =>
      normalizedCnpj.length === 14
        ? { ...office, formattedCnpjForForm: formatCnpj(normalizedCnpj) }
        : office;

    // Navegar para step-1 recria o componente: o preenchimento precisa ir para o draft e ser aplicado na nova instância.
    if (validationResult.officeData) {
      this.partnerInscriptionDraft.setPendingCnpjOfficePrefill(withCnpjForNextScreen(validationResult.officeData));
      goToCompanyStep();
      return;
    }

    if (normalizedCnpj.length === 14) {
      this.isLoadingCNPJ = true;
      this.cnpjLookupService
        .fetchOffice(normalizedCnpj)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
            this.partnerInscriptionDraft.setPendingCnpjOfficePrefill(withCnpjForNextScreen(data));
            this.isLoadingCNPJ = false;
            goToCompanyStep();
          },
          error: () => {
            this.isLoadingCNPJ = false;
            this.partnerInscriptionDraft.setPendingCnpjOfficePrefill(
              withCnpjForNextScreen({})
            );
            goToCompanyStep();
          },
        });
      return;
    }

    goToCompanyStep();
  }

  /**
   * Dados da consulta CNPJ (CNPJa/Brasil API) guardados antes da troca de rota comece-aqui → step-1.
   */
  private applyPendingCnpjOfficePrefill(): void {
    if (this.wizardStep < 0) {
      return;
    }
    const pending = this.partnerInscriptionDraft.consumePendingCnpjOfficePrefill();
    if (!pending) {
      return;
    }
    this.applyOfficeLookupData(pending);
  }

  private loadInscriptionSteps(): void {
    this.inscriptionSteps = [
      {
        icon: 'mdn-Icon-documento',
        label: this.i18n.instant('pages.partnerRegistration.inscription.steps.company'),
      },
      {
        icon: 'mdn-Icon-empresa',
        label: this.i18n.instant('pages.partnerRegistration.inscription.steps.address'),
      },
      {
        icon: 'mdn-Icon-check',
        label: this.i18n.instant('pages.partnerRegistration.inscription.steps.questionnaire'),
      },
    ];
  }

  get stepperIndex(): number {
    return this.showSuccessScreen ? 2 : this.wizardStep;
  }

  get stepperLabelCurrent(): number {
    return this.showSuccessScreen ? 3 : this.wizardStep + 1;
  }

  private syncSuccessScreenFromRoute(): void {
    const isSuccess = this.route.snapshot.url.some(segment => segment.path === 'success');
    this.showSuccessScreen = isSuccess;
    if (isSuccess) {
      this.wizardStep = 2;
    }
  }

  get isCurrentStepValid(): boolean {
    if (this.wizardStep === 0) {
      return this.areControlsValid(this.step0Keys);
    }
    if (this.wizardStep === 1) {
      return this.areControlsValid(this.step1Keys);
    }
    return true;
  }

  get currentCardTitle(): string {
    const base = 'pages.partnerRegistration.inscription.wizard.cardTitle';
    if (this.wizardStep === 1) return this.i18n.instant(`${base}.step2`);
    if (this.wizardStep === 2) return this.i18n.instant(`${base}.step3`);
    return this.i18n.instant(`${base}.step1`);
  }

  get currentCardDescription(): string {
    const base = 'pages.partnerRegistration.inscription.wizard.cardDescription';
    if (this.wizardStep === 1) return this.i18n.instant(`${base}.step2`);
    if (this.wizardStep === 2) return this.i18n.instant(`${base}.step3`);
    return this.i18n.instant(`${base}.step1`);
  }

  onCancelInscription(): void {
    void this.router.navigate(['/cadastro-parceiros']);
  }

  onWizardContinue(): void {
    if (!this.validateCurrentStep() || this.isSubmitting) {
      return;
    }

    this.saveCurrentStepAndContinue();
  }

  onWizardBack(): void {
    if (this.wizardStep > 0) {
      this.navigateToWizardStep(this.wizardStep - 1);
    }
  }

  private validateCurrentStep(): boolean {
    if (this.wizardStep === 0) {
      this.markControlsTouched(this.step0Keys);
      return this.areControlsValid(this.step0Keys);
    }

    if (this.wizardStep === 1) {
      this.markControlsTouched(this.step1Keys);
      return this.areControlsValid(this.step1Keys);
    }

    return true;
  }

  private saveCurrentStepAndContinue(): void {
    this.isSubmitting = true;

    const backendStep = this.wizardStep + 1;
    const payload = this.buildStepSavePayload(backendStep);

    this.partnerService
      .saveInscriptionDraft(payload, backendStep)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const mergedDraft = this.mergeDraftWithFormAfterSave(response);
          this.patchFormFromBackend(mergedDraft);
          const t = this.partnerInscriptionDraft.getActiveToken();
          if (t) {
            this.partnerInscriptionDraft.set(t, mergedDraft);
          } else {
            this.partnerInscriptionDraft.setLatest(mergedDraft);
          }
          const nextStep = Math.min(this.wizardStep + 1, this.stepRoutes.length - 1);

          this.isSubmitting = false;
          this.navigateToWizardStep(nextStep);
        },
        error: (error) => {
          console.error('Erro ao salvar etapa do cadastro de parceiros:', error);
          this.toastService.showError(error.error.msg ||
            this.i18n.instant('pages.partnerRegistration.errors.unexpected'),
            5000,
            'top-center'
          );
          this.isSubmitting = false;
        },
      });
  }

  /**
   * Monta o payload completo esperado pelo back-end em todos os steps.
   * Campos ainda não coletados até o step atual são enviados como null/'' ou estrutura vazia,
   * mantendo sempre o mesmo contrato da requisição (ver /partners-integration?draft=true&step=N).
   */
  private buildStepSavePayload(step: number): PartnerInscriptionSaveRequest {
    // getRawValue inclui campos desabilitados (CNPJ, razão social, etc. quando em retomada via token).
    const formValue = this.partnerForm.getRawValue();
    const cachedDraft = this.getCachedDraft();

    const hasStep1 = step >= 1;
    const hasStep2 = step >= 2;
    const hasStep3 = step >= 3;

    const phoneDigits = this.cleanDocument(formValue.telefone ?? '');
    const normalizedPhone =
      hasStep1 && phoneDigits.length >= 10 && phoneDigits.length <= 11 ? phoneDigits : null;

    const cnpjDigits = unformatCnpj(formValue.cnpj ?? '');
    const legalName = hasStep1 ? (formValue.razaoSocial ?? '').trim() : '';
    const setorAtuacao = hasStep1 ? (formValue.setorAtuacao ?? '').trim() : '';
    const nomeResponsavel = hasStep1 ? (formValue.nomeResponsavel ?? '').trim() : '';

    return {
      id: cachedDraft?.id ?? null,
      uuid: cachedDraft?.uuid ?? null,
      legalName,
      responsibleName: nomeResponsavel,
      segment: setorAtuacao,
      cnpj: hasStep1 ? cnpjDigits : '',
      email: hasStep1 ? (formValue.email ?? '').trim() : '',
      street: hasStep2 ? (formValue.endereco ?? '').trim() : '',
      zipCode: hasStep2 ? this.cleanDocument(formValue.cep ?? '') : '',
      number: hasStep2 ? (formValue.numero ?? '').trim() : '',
      complement: hasStep2 ? (formValue.complemento ?? '').trim() : '',
      neighborhood: hasStep2 ? (formValue.bairro ?? '').trim() : '',
      city: hasStep2 ? (formValue.cidade ?? '').trim() : '',
      state: hasStep2 ? (formValue.estado ?? '').trim() : '',
      phoneNumber: normalizedPhone,
      step,
      token: !!this.partnerInscriptionDraft.getActiveToken(),
      startupStage: hasStep3 ? (formValue.startupStage ?? null) : null,
      businessType: hasStep3 ? (formValue.businessType ?? null) : null,
      revenueModel: hasStep3 ? (formValue.revenueModel ?? null) : null,
      mainSegment: hasStep3 ? (formValue.mainSegment ?? null) : null,
      operationScale: hasStep3 ? (formValue.operationScale ?? null) : null,
    };
  }

  private getCachedDraft(): PartnerInscriptionData | undefined {
    const token = this.partnerInscriptionDraft.getActiveToken();
    if (token) {
      const byToken = this.partnerInscriptionDraft.get(token);
      if (byToken) {
        return byToken;
      }
    }
    return this.partnerInscriptionDraft.getLatest();
  }

  private syncWizardStepFromRoute(): void {
    const stepSegment = this.route.snapshot.url[this.route.snapshot.url.length - 1]?.path ?? '';
    if (stepSegment === 'comece-aqui') {
      this.wizardStep = -1;
      this.partnerInscriptionDraft.setCurrentWizardStepRoute(null);
      return;
    }

    this.wizardStep = this.getStepIndexByRoute(stepSegment);
    if (stepSegment === 'step-1' || stepSegment === 'step-2' || stepSegment === 'step-3') {
      this.partnerInscriptionDraft.setCurrentWizardStepRoute(stepSegment as 'step-1' | 'step-2' | 'step-3');
    }
  }

  private getStepIndexByRoute(stepRoute: string): number {
    const stepIndex = this.stepRoutes.findIndex(route => route === stepRoute);
    return stepIndex === -1 ? 0 : stepIndex;
  }

  private getStepRouteByIndex(stepIndex: number): string {
    return this.stepRoutes[stepIndex] ?? this.stepRoutes[0];
  }

  private normalizeBackendStep(step: number): number {
    const lastStep = this.stepRoutes.length;
    const boundedStep = Math.max(1, Math.min(step, lastStep));
    return boundedStep - 1;
  }

  private navigateToWizardStep(stepIndex: number, replaceUrl = false): void {
    const stepRoute = this.getStepRouteByIndex(stepIndex);
    this.wizardStep = this.getStepIndexByRoute(stepRoute);
    this.partnerInscriptionDraft.allowWizardRouteChange();
    this.partnerInscriptionStepGuard.grantStepAccess(stepRoute);

    void this.router.navigate(['/cadastro-parceiros/inscricao', stepRoute], {
      replaceUrl,
    });
  }

  /**
   * Mapeia a rota atual para o parâmetro `step` da API (1 = dados da empresa, 2 = endereço, 3 = produto).
   * Em `comece-aqui` usa 1 na primeira carga (token vindo do e-mail / fluxo inicial).
   */
  private getBackendStepForRequest(): number {
    const stepSegment = this.route.snapshot.url[this.route.snapshot.url.length - 1]?.path ?? '';
    if (stepSegment === 'comece-aqui') {
      return 1;
    }
    const idx = this.stepRoutes.findIndex(route => route === stepSegment);
    if (idx >= 0) {
      return idx + 1;
    }
    return 1;
  }

  private tryHydrateFromDraftCache(): void {
    const cached = this.getCachedDraft();
    if (!cached) {
      return;
    }
    this.patchFormFromBackend(cached);
    this.applyStep0TokenLock();
  }

  private loadInscriptionFromToken(token: string, step: number): void {
    const shouldAutoRedirect = this.getWizardStepIndexFromRoute() === -1;
    const cached = this.partnerInscriptionDraft.get(token);
    if (cached) {
      this.applyLoadedInscriptionData(cached, shouldAutoRedirect);
      return;
    }

    this.partnerService
      .validateToken(token, step)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.partnerInscriptionDraft.set(token, response);
          this.applyLoadedInscriptionData(response, shouldAutoRedirect);
        },
        error: (error) => {
          console.error('Erro ao recuperar dados do parceiro pelo token:', error);
        },
      });
  }

  private applyLoadedInscriptionData(
    data: PartnerInscriptionData,
    adjustRouteToFirstIncomplete = true
  ): void {
    this.patchFormFromBackend(data);
    this.applyStep0TokenLock();
    if (!adjustRouteToFirstIncomplete) {
      return;
    }
    const targetStep = this.getTargetWizardStepIndexAfterLoad(data);
    const currentStep = this.getWizardStepIndexFromRoute();
    if (targetStep !== currentStep) {
      this.navigateToWizardStep(targetStep, true);
    }
  }

  /**
   * Quando o cadastro é retomado via token (link do e-mail ou input de token),
   * o back-end já validou CNPJ/razão social/setor/e-mail, então esses campos do
   * step 1 ficam somente leitura; permanecem editáveis apenas telefone e nome
   * do responsável.
   */
  private applyStep0TokenLock(): void {
    const hasActiveToken = !!this.partnerInscriptionDraft.getActiveToken();
    this.step0LockedKeys.forEach((key) => {
      const control = this.partnerForm.get(key);
      if (!control) {
        return;
      }
      if (hasActiveToken) {
        if (!control.disabled) {
          control.disable({ emitEvent: false });
        }
      } else if (control.disabled) {
        control.enable({ emitEvent: false });
      }
    });
  }

  /** Garante que o draft em memória reflita o que o usuário acabou de salvar (a API pode omitir campos). */
  private mergeDraftWithFormAfterSave(response: PartnerInscriptionData): PartnerInscriptionData {
    const fv = this.partnerForm.getRawValue();
    const cached = this.getCachedDraft();
    const phoneDigits = this.cleanDocument(fv.telefone ?? '');
    const product: PartnerInscriptionProduct = response.product ?? {
      fileUrl: null,
      fileId: null,
      name: '',
      description: '',
      shortDescription: '',
      variations: [],
    };

    return {
      ...response,
      id: response.id ?? cached?.id ?? null,
      uuid: response.uuid ?? cached?.uuid ?? null,
      createdAt: response.createdAt ?? cached?.createdAt ?? null,
      cnpj: unformatCnpj(fv.cnpj ?? '') || response.cnpj,
      legalName: (fv.razaoSocial ?? '').trim() || response.legalName,
      segment: (fv.setorAtuacao ?? '').trim() || response.segment,
      responsibleName: (fv.nomeResponsavel ?? '').trim() || response.responsibleName,
      email: (fv.email ?? '').trim() || response.email,
      phoneNumber: phoneDigits.length >= 10 && phoneDigits.length <= 11 ? phoneDigits : response.phoneNumber,
      zipCode: this.cleanDocument(fv.cep ?? '') || response.zipCode,
      street: (fv.endereco ?? '').trim() || response.street,
      number: (fv.numero ?? '').trim() || response.number,
      complement: (fv.complemento ?? '').trim() ?? response.complement,
      neighborhood: (fv.bairro ?? '').trim() || response.neighborhood,
      city: (fv.cidade ?? '').trim() || response.city,
      state: (fv.estado ?? '').trim() || response.state,
      urlDocumentation: (fv.urlProduto ?? '').trim() || response.urlDocumentation,
      acceptanceTermsId: fv.termosAceitos === true ? '1' : response.acceptanceTermsId,
      product: {
        ...product,
        description: (fv.descricaoProduto ?? '').trim() || product.description,
      },
    };
  }


  private getWizardStepIndexFromRoute(): number {
    const stepSegment = this.route.snapshot.url[this.route.snapshot.url.length - 1]?.path ?? '';
    if (stepSegment === 'comece-aqui') {
      return -1;
    }
    const idx = this.stepRoutes.findIndex(route => route === stepSegment);
    return idx === -1 ? 0 : idx;
  }


  private getFirstStrictlyIncompleteWizardIndex(d: PartnerInscriptionData): number | null {
    if (!this.isApiCompanyStepComplete(d)) {
      return 0;
    }
    if (!this.isApiAddressStepComplete(d)) {
      return 1;
    }
    if (!this.isApiProductStepComplete(d)) {
      return 2;
    }
    return null;
  }

  private getTargetWizardStepIndexAfterLoad(d: PartnerInscriptionData): number {
    const apiIndex = this.normalizeBackendStep(d.step ?? 1);
    const firstGap = this.getFirstStrictlyIncompleteWizardIndex(d);
    if (firstGap === null) {
      return apiIndex;
    }
    return Math.min(apiIndex, firstGap);
  }

  private isApiCompanyStepComplete(d: PartnerInscriptionData): boolean {
    const cnpjDigits = unformatCnpj(d.cnpj ?? '');
    const cnpjDisplay = cnpjDigits.length === 14 ? formatCnpj(cnpjDigits) : (d.cnpj ?? '');
    const cnpjCtrl = new FormControl(cnpjDisplay, [Validators.required, CustomValidators.cnpj]);
    const razaoCtrl = new FormControl((d.legalName ?? '').trim(), [
      Validators.required,
      CustomValidators.noWhitespaceOnly,
      Validators.maxLength(255),
    ]);
    const nomeFantasiaVal = (d.segment ?? '').trim() || (d.responsibleName ?? '').trim();
    const nomeCtrl = new FormControl(nomeFantasiaVal, [
      Validators.required,
      CustomValidators.noWhitespaceOnly,
      Validators.maxLength(255),
    ]);
    const setorAtuacaoVal = (d.segment ?? '').trim() || nomeFantasiaVal;
    const setorAtuacaoCtrl = new FormControl(setorAtuacaoVal, [
      Validators.required,
      CustomValidators.noWhitespaceOnly,
      Validators.maxLength(255),
    ]);
    const nomeResponsavelVal = (d.responsibleName ?? '').trim() || nomeFantasiaVal;
    const nomeResponsavelCtrl = new FormControl(nomeResponsavelVal, [
      Validators.required,
      CustomValidators.noWhitespaceOnly,
      Validators.maxLength(255),
    ]);
    const phoneDigits = String(d.phoneNumber ?? '').replace(/\D/g, '');
    const telefoneVal =
      phoneDigits.length >= 10 && phoneDigits.length <= 11 ? this.formatPhoneBr(phoneDigits) : '';
    const telCtrl = new FormControl(telefoneVal, [Validators.required, CustomValidators.phone]);
    const emailCtrl = new FormControl((d.email ?? '').trim(), [
      Validators.required,
      Validators.email,
      CustomValidators.emailNoSpaces,
      CustomValidators.emailWithDomainValidator(),
      Validators.maxLength(255),
    ]);
    return (
      !!cnpjCtrl.valid &&
      !!razaoCtrl.valid &&
      !!nomeCtrl.valid &&
      !!setorAtuacaoCtrl.valid &&
      !!nomeResponsavelCtrl.valid &&
      !!telCtrl.valid &&
      !!emailCtrl.valid
    );
  }

  private isApiAddressStepComplete(d: PartnerInscriptionData): boolean {
    const cepDigits = this.cleanDocument(d.zipCode ?? '');
    const cepDisplay = cepDigits.length === 8 ? formatZipCode(cepDigits) : (d.zipCode ?? '');
    const cepCtrl = new FormControl(cepDisplay, [Validators.required, CustomValidators.cep]);
    const enderecoCtrl = new FormControl((d.street ?? '').trim(), [
      Validators.required,
      Validators.maxLength(255),
    ]);
    const numeroCtrl = new FormControl((d.number ?? '').trim(), [
      Validators.required,
      Validators.maxLength(20),
    ]);
    const bairroCtrl = new FormControl((d.neighborhood ?? '').trim(), [
      Validators.required,
      Validators.maxLength(100),
    ]);
    const cidadeCtrl = new FormControl((d.city ?? '').trim(), [
      Validators.required,
      Validators.maxLength(100),
    ]);
    const estadoCtrl = new FormControl((d.state ?? '').trim(), [Validators.required]);
    return (
      !!cepCtrl.valid &&
      !!enderecoCtrl.valid &&
      !!numeroCtrl.valid &&
      !!bairroCtrl.valid &&
      !!cidadeCtrl.valid &&
      !!estadoCtrl.valid
    );
  }

  private isApiProductStepComplete(d: PartnerInscriptionData): boolean {
    const p = d.product;
    const desc =
      (p?.description ?? '').trim() ||
      (p?.shortDescription ?? '').trim() ||
      (p?.name ?? '').trim();
    const descCtrl = new FormControl(desc, [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(255),
    ]);
    const hasFile = !!(p?.fileUrl ?? p?.fileId);
    const termsOk =
      d.acceptanceTermsId != null && String(d.acceptanceTermsId).trim().length > 0;
    return !!descCtrl.valid && hasFile && termsOk;
  }

  private patchFormFromBackend(data: PartnerInscriptionData): void {
    const cnpjDigits = unformatCnpj(data?.cnpj ?? '');
    const cepDigits = this.cleanDocument(data?.zipCode ?? '');
    const segment = (data?.segment ?? '').trim();
    const responsible = (data?.responsibleName ?? '').trim();
    const nomeFantasia = segment || responsible;
    const setorAtuacao = segment || nomeFantasia;
    const nomeResponsavel = responsible || nomeFantasia;

    const phoneRaw = data?.phoneNumber;
    const telefone =
      phoneRaw != null && String(phoneRaw).replace(/\D/g, '').length >= 10
        ? this.formatPhoneBr(String(phoneRaw))
        : this.partnerForm.get('telefone')?.value ?? '';

    const product = data?.product;
    const descricao =
      (product?.description ?? '').trim() ||
      (product?.shortDescription ?? '').trim() ||
      (product?.name ?? '').trim();

    const urlDoc = data?.urlDocumentation ?? '';
    const fileUrl = product?.fileUrl ?? '';
    const urlProduto = (typeof urlDoc === 'string' ? urlDoc.trim() : '') || (typeof fileUrl === 'string' ? fileUrl.trim() : '') || '';
    const hasProductFile = !!(product?.fileUrl ?? product?.fileId);
    const termsAccepted =
      data.acceptanceTermsId != null && String(data.acceptanceTermsId).trim().length > 0;

    this.partnerForm.patchValue(
      {
        cnpj: cnpjDigits.length === 14 ? formatCnpj(cnpjDigits) : (data?.cnpj ?? ''),
        razaoSocial: (data?.legalName ?? '').trim(),
        nomeFantasia,
        setorAtuacao,
        nomeResponsavel,
        telefone,
        email: (data?.email ?? '').trim(),
        cep: cepDigits.length === 8 ? formatZipCode(cepDigits) : (data?.zipCode ?? ''),
        endereco: (data?.street ?? '').trim(),
        numero: (data?.number ?? '').trim(),
        complemento: (data?.complement ?? '').trim(),
        bairro: (data?.neighborhood ?? '').trim(),
        cidade: (data?.city ?? '').trim(),
        estado: (data?.state ?? '').trim(),
        descricaoProduto: descricao,
        urlProduto,
        arquivo: hasProductFile ? 'enviado' : '',
        termosAceitos: termsAccepted,
      },
      { emitEvent: false }
    );
  }

  private formatPhoneBr(phone: string): string {
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    }
    if (numbers.length === 10) {
      return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }
    return phone.trim();
  }

  private markControlsTouched(keys: readonly string[]): void {
    keys.forEach(k => this.partnerForm.get(k)?.markAsTouched());
  }

  private areControlsValid(keys: readonly string[]): boolean {
    return keys.every(k => {
      const c = this.partnerForm.get(k);
      // Controles desabilitados (ex.: CNPJ/razão social travados em retomada via token)
      // têm status DISABLED, então `.valid` é false — tratamos como válidos para não
      // travar o botão "Continuar" quando os campos já foram validados pelo back-end.
      return !!c && (c.valid || c.disabled);
    });
  }

  private isProductStepValid(): boolean {
    if (this.selectedFiles.length === 0) {
      this.partnerForm.get('arquivo')?.setErrors({ required: true });
      this.partnerForm.get('arquivo')?.markAsTouched();
    }
    this.partnerForm.get('descricaoProduto')?.markAsTouched();
    this.partnerForm.get('termosAceitos')?.markAsTouched();
    return (
      this.areControlsValid(['descricaoProduto', 'termosAceitos']) &&
      this.selectedFiles.length > 0 &&
      !!this.partnerForm.get('termosAceitos')?.value
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.langSubscription) {
      this.langSubscription.unsubscribe();
    }
  }

  buscarEndereco(cep: string): void {
    this.isLoadingCEP = true;
    const cleanCEP = cep.replace(/\D/g, '');
    
    this.viaCEPService.buscarCEP(cleanCEP).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (response: ViaCEPResponse) => {
        if (!response.erro) {
          this.partnerForm.patchValue({
            endereco: response.logradouro,
            bairro: response.bairro,
            cidade: response.localidade,
            estado: response.uf
          });
        } else {
          this.toastService.showError(this.i18n.instant('pages.partnerRegistration.errors.cepNotFound'), 5000, 'top-center');
        }
        this.isLoadingCEP = false;
      },
      error: (error) => {
        console.error('Erro ao buscar CEP:', error);
        this.toastService.showError(this.i18n.instant('pages.partnerRegistration.errors.cepError'), 5000, 'top-center');
        this.isLoadingCEP = false;
      }
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
    
    // Atualiza a exibição dos nomes
    if (this.selectedFiles.length > 0) {
      if (this.selectedFiles.length === 1) {
        this.fileNames = this.selectedFiles[0].name;
      } else {
        this.fileNames = `${this.selectedFiles.length} arquivo(s) selecionado(s)`;
      }
    } else {
      this.fileNames = this.i18n.instant('pages.partnerRegistration.form.sections.product.fileUpload.noFile');
    }

    // Atualiza o formulário
    if (this.selectedFiles.length > 0) {
      const fileNamesString = this.selectedFiles.map(f => f.name).join(', ');
      this.partnerForm.patchValue({ arquivo: fileNamesString });
      this.partnerForm.get('arquivo')?.setErrors(null);
    } else {
      this.partnerForm.patchValue({ arquivo: '' });
      this.partnerForm.get('arquivo')?.setErrors({ required: true });
      this.partnerForm.get('arquivo')?.markAsTouched();
    }
    
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const maxFiles = 3;
      
      // Verifica se já atingiu o limite de arquivos
      if (this.selectedFiles.length >= maxFiles) {
        this.toastService.showError(
          this.i18n.instant('pages.partnerRegistration.form.sections.product.fileUpload.maxFiles'),
          3000,
          'top-center'
        );
        if (this.fileInput) {
          this.fileInput.nativeElement.value = '';
        }
        return;
      }

      const validExtensions = ['.pdf', '.jpg', '.jpeg', '.doc', '.docx', '.png', '.ppt', '.pptx'];
      const maxFileSize = 3 * 1024 * 1024; // 3MB
      const files: File[] = Array.from(input.files);
      const validFiles: File[] = [];
      const invalidFiles: string[] = [];
      let allErrorsAreSizeErrors = true;

      // Calcula quantos arquivos ainda podem ser adicionados
      const remainingSlots = maxFiles - this.selectedFiles.length;

      files.forEach((file, index) => {
        // Verifica se já atingiu o limite considerando os arquivos já selecionados
        if (this.selectedFiles.length + validFiles.length >= maxFiles) {
          invalidFiles.push(`${file.name} - ${this.i18n.instant('pages.partnerRegistration.form.sections.product.fileUpload.maxFiles')}`);
          allErrorsAreSizeErrors = false;
          return;
        }

        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
          invalidFiles.push(`${file.name} - ${this.i18n.instant('pages.partnerRegistration.form.sections.product.fileUpload.invalidFormat')}`);
          allErrorsAreSizeErrors = false;
          return;
        }

        if (file.size > maxFileSize) {
          invalidFiles.push(this.i18n.instant('pages.partnerRegistration.form.sections.product.fileUpload.maxSize'));
          return;
        }

        validFiles.push(file);
      });

      if (invalidFiles.length > 0) {
        if (allErrorsAreSizeErrors) {
          this.toastService.showError(this.i18n.instant('pages.partnerRegistration.form.sections.product.fileUpload.maxSize'), 5000, 'top-center');
        } else {
          this.toastService.showError(invalidFiles.join('\n'), 5000, 'top-center');
        }
      }

      // Adiciona os arquivos válidos à lista (evita duplicatas)
      validFiles.forEach(file => {
        if (!this.selectedFiles.find(f => f.name === file.name && f.size === file.size)) {
          this.selectedFiles.push(file);
        }
      });
      
      // Limpa o input se nenhum arquivo válido foi adicionado
      if (validFiles.length === 0 && this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }

      // Atualiza a exibição dos nomes
      if (this.selectedFiles.length > 0) {
        if (this.selectedFiles.length === 1) {
          this.fileNames = this.selectedFiles[0].name;
        } else {
          this.fileNames = `${this.selectedFiles.length} arquivo(s) selecionado(s)`;
        }
      } else {
        this.fileNames = this.i18n.instant('pages.partnerRegistration.form.sections.product.fileUpload.noFile');
      }

      // Atualiza o formulário com os nomes dos arquivos
      if (this.selectedFiles.length > 0) {
        const fileNamesString = this.selectedFiles.map(f => f.name).join(', ');
        this.partnerForm.patchValue({ arquivo: fileNamesString });
        this.partnerForm.get('arquivo')?.setErrors(null);
      } else {
        this.partnerForm.patchValue({ arquivo: '' });
        this.partnerForm.get('arquivo')?.setErrors({ required: true });
      }

      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  onCnpjSearchClick(): void {
    const raw = this.partnerForm.get('cnpj')?.value ?? '';
    const clean = unformatCnpj(raw);
    if (clean.length === 14 && this.partnerForm.get('cnpj')?.valid) {
      this.fetchCNPJInfo(clean);
    }
  }

  onCepSearchClick(): void {
    const cep = this.partnerForm.get('cep')?.value ?? '';
    if (cep && this.viaCEPService.validarCEP(cep)) {
      this.buscarEndereco(cep);
    }
  }

  realizarNovoCadastro(): void {
    this.showSuccessScreen = false;
    this.partnerInscriptionDraft.clearActiveToken();
    this.applyStep0TokenLock();
    void this.router.navigate(['/cadastro-parceiros/inscricao/comece-aqui'], {
      replaceUrl: true,
    });
  }

  irParaAcompanharStatus(): void {
    this.showSuccessScreen = false;
    this.wizardStep = 0;
    void this.router.navigate(['/cadastro-parceiros'], {
      queryParams: { tab: 'acompanhar-status' },
      replaceUrl: true,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  goToPartnerPortal(): void {
    window.open(
      'https://www.claro.com.br/empresas/hubdesolucoes/partnerportal/login',
      '_blank',
      'noopener,noreferrer'
    );
  }

  /**
   * Submit final do step 3 (questionário sobre a startup).
   * Envia todos os dados coletados e redireciona para a tela de sucesso.
   */
  onStep3Submit(): void {
    if (this.isSubmitting) {
      return;
    }

    const questionnaireKeys = [
      'startupStage', 'businessType', 'revenueModel',
      'mainSegment', 'operationScale',
    ];
    questionnaireKeys.forEach(k => this.partnerForm.get(k)?.markAsTouched());

    const allValid = questionnaireKeys.every(k => {
      const ctrl = this.partnerForm.get(k);
      return ctrl?.valid && ctrl.value;
    });
    if (!allValid) return;

    this.isSubmitting = true;
    const payload = this.buildStepSavePayload(3);

    this.partnerService
      .submitInscription(payload, 3)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.patchFormFromBackend(response);
          const finalDraft = this.mergeDraftWithFormAfterSave(response);
          const token = this.partnerInscriptionDraft.getActiveToken();
          if (token) {
            this.partnerInscriptionDraft.set(token, finalDraft);
          } else {
            this.partnerInscriptionDraft.setLatest(finalDraft);
          }
          this.isSubmitting = false;
          this.partnerInscriptionSuccessGuard.grantAccess();
          this.partnerInscriptionDraft.allowWizardRouteChange();
          void this.router.navigate(['/cadastro-parceiros/inscricao/step-3/success'], {
            queryParamsHandling: 'preserve',
            replaceUrl: true,
          });
        },
        error: (error) => {
          console.error('Erro ao enviar cadastro final de parceiros:', error);
          let errorMessage = error.error?.msg || this.i18n.instant(
            'pages.partnerRegistration.errors.unexpected'
          );
          if (error?.status === 0) {
            errorMessage = error.error?.msg || this.i18n.instant(
              'pages.partnerRegistration.errors.connection'
            );
          } else if (error?.status === 400) {
            errorMessage = error.error?.msg || this.i18n.instant(
              'pages.partnerRegistration.errors.invalidData'
            );
          } else if (error?.status >= 500) {
            errorMessage = error.error?.msg || this.i18n.instant(
              'pages.partnerRegistration.errors.server'
            );
          }
          this.toastService.showError(errorMessage, 5000, 'top-center');
          this.isSubmitting = false;
        },
      });
  }

  submitPartnerForm(): void {
    if (!this.isProductStepValid()) {
      return;
    }
    this.onSubmit();
  }

  onSubmit(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    if (this.selectedFiles.length === 0) {
      this.partnerForm.get('arquivo')?.setErrors({ required: true });
      this.partnerForm.get('arquivo')?.markAsTouched();
    }

    if (this.partnerForm.invalid || this.isSubmitting) {
      this.markFormGroupTouched();
      return;
    }

    this.isSubmitting = true;

    const backendStep = this.wizardStep + 1;
    const payload = this.buildStepSavePayload(backendStep);

    this.partnerService
      .submitInscription(payload, backendStep)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.partnerInscriptionSuccessGuard.grantAccess();

          this.partnerForm.reset();
          this.selectedFiles = [];
          this.fileNames = this.i18n.instant('pages.partnerRegistration.form.sections.product.fileUpload.noFile');
          if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
          }

          this.partnerInscriptionDraft.allowWizardRouteChange();
          void this.router.navigate(['/cadastro-parceiros/inscricao/step-3/success'], {
            queryParamsHandling: 'preserve',
            replaceUrl: true,
          });
        },
        error: (error) => {
          console.error('Error submitting partner inscription:', error);
          let errorMessage = 'Erro ao enviar cadastro. Por favor, tente novamente.';
          
          if (error.status === 0) {
            errorMessage = this.i18n.instant('pages.partnerRegistration.errors.connection');
          } else if (error.status === 400) {
            errorMessage = this.i18n.instant('pages.partnerRegistration.errors.invalidData');
          } else if (error.status === 413) {
            errorMessage = this.i18n.instant('pages.partnerRegistration.form.sections.product.fileUpload.maxSize');
          } else if (error.status >= 500) {
            errorMessage = this.i18n.instant('pages.partnerRegistration.errors.server');
          }
          
          this.toastService.showError(errorMessage, 5000, 'top-center');
          this.isSubmitting = false;
        },
      });
  }

  preventLeadingSpace(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    if (event.key === ' ' && input.selectionStart === 0) {
      event.preventDefault();
    }
  }

  private markFormGroupTouched(): void {
    Object.keys(this.partnerForm.controls).forEach(key => {
      const control = this.partnerForm.get(key);
      control?.markAsTouched();
    });
  }

  // Getters for form controls
  get cnpjControl() { return this.partnerForm.get('cnpj'); }
  get razaoSocialControl() { return this.partnerForm.get('razaoSocial'); }
  get nomeFantasiaControl() { return this.partnerForm.get('nomeFantasia'); }
  get setorAtuacaoControl() { return this.partnerForm.get('setorAtuacao'); }
  get nomeResponsavelControl() { return this.partnerForm.get('nomeResponsavel'); }
  get telefoneControl() { return this.partnerForm.get('telefone'); }
  get emailControl() { return this.partnerForm.get('email'); }
  get cepControl() { return this.partnerForm.get('cep'); }
  get enderecoControl() { return this.partnerForm.get('endereco'); }
  get numeroControl() { return this.partnerForm.get('numero'); }
  get complementoControl() { return this.partnerForm.get('complemento'); }
  get bairroControl() { return this.partnerForm.get('bairro'); }
  get cidadeControl() { return this.partnerForm.get('cidade'); }
  get estadoControl() { return this.partnerForm.get('estado'); }
  get descricaoProdutoControl() { return this.partnerForm.get('descricaoProduto'); }
  get urlProdutoControl() { return this.partnerForm.get('urlProduto'); }
  get arquivoControl() { return this.partnerForm.get('arquivo'); }
  get termosAceitosControl() { return this.partnerForm.get('termosAceitos'); }

  /** Controle compartilhado com o passo 3 (checkbox Termos e Condições). */
  get termosAceitosStep3Control(): FormControl<boolean | null> {
    return this.partnerForm.get('termosAceitos') as FormControl<boolean | null>;
  }

  private cleanDocument(document: string): string {
    return document.replace(/\D/g, '');
  }

  private fetchCNPJInfo(cnpj: string): void {
    this.isLoadingCNPJ = true;

    this.cnpjLookupService.fetchOffice(cnpj)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => this.handleCNPJDataSuccess(data),
        error: err => this.handleCNPJDataError(err),
      });
  }

  private handleCNPJDataSuccess(data: CnpjOfficeData): void {
    this.applyOfficeLookupData(data);
    this.isLoadingCNPJ = false;
  }

  private applyOfficeLookupData(data: CnpjOfficeData): void {
    if (data.formattedCnpjForForm) {
      this.partnerForm.patchValue({ cnpj: data.formattedCnpjForForm }, { emitEvent: false });
      this.partnerForm.get('cnpj')?.setErrors(null);
    }
    this.fillCompanyData(data);
    this.fillAddressData(data);
    this.fillContactData(data);
    this.fillProductStepData(data);
  }

  /** Etapa 3 do wizard: descrição do produto (CNAE / atividade quando a API trouxer). */
  private fillProductStepData(data: CnpjOfficeData): void {
    const hint = data.suggestedProductDescription?.trim();
    if (!hint || hint.length < 10) {
      return;
    }
    const current = String(this.partnerForm.get('descricaoProduto')?.value ?? '').trim();
    if (current.length > 0) {
      return;
    }
    this.partnerForm.patchValue({ descricaoProduto: hint }, { emitEvent: false });
    this.partnerForm.get('descricaoProduto')?.setErrors(null);
  }

  private handleCNPJDataError(err: any): void {
    console.error('Erro ao buscar CNPJ:', err);
    this.isLoadingCNPJ = false;
  }

  private fillCompanyData(data: CnpjOfficeData): void {
    if (data?.company) {
      const razaoSocial = data.company.name || '';
      const nomeFantasia = (data.alias || data.company.name || '').trim();
      const setorAtuacao = (data.mainActivityText || nomeFantasia).trim();
      const nomeResponsavel = (data.responsibleName || nomeFantasia).trim();

      this.partnerForm.patchValue(
        { razaoSocial, nomeFantasia, setorAtuacao, nomeResponsavel },
        { emitEvent: false }
      );

      this.partnerForm.get('razaoSocial')?.setErrors(null);
      this.partnerForm.get('nomeFantasia')?.setErrors(null);
      this.partnerForm.get('setorAtuacao')?.setErrors(null);
      this.partnerForm.get('nomeResponsavel')?.setErrors(null);
    }
  }

  private fillContactData(data: CnpjOfficeData): void {
    const patch: { telefone?: string; email?: string } = {};

    if (data.phoneDigits && data.phoneDigits.length >= 10 && data.phoneDigits.length <= 11) {
      patch.telefone = this.formatPhoneMaskFromDigits(data.phoneDigits);
    }
    if (data.email && data.email.trim()) {
      patch.email = data.email.trim();
    }

    if (Object.keys(patch).length === 0) {
      return;
    }

    this.partnerForm.patchValue(patch, { emitEvent: false });
    if (patch.telefone) {
      this.partnerForm.get('telefone')?.setErrors(null);
    }
    if (patch.email) {
      this.partnerForm.get('email')?.setErrors(null);
    }
  }

  private formatPhoneMaskFromDigits(digits: string): string {
    if (digits.length === 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  private fillAddressData(data: CnpjOfficeData): void {
    if (data?.address) {
      const cep = data.address.zip ? data.address.zip.replace(/\D/g, '') : '';
      const formattedCep = cep.length === 8 ? `${cep.substring(0, 5)}-${cep.substring(5)}` : '';

      this.partnerForm.patchValue({
        endereco: data.address.street || '',
        numero: data.address.number || '',
        complemento: data.address.details || '',
        bairro: data.address.district || '',
        cep: formattedCep,
        cidade: data.address.city || '',
        estado: data.address.state || ''
      }, { emitEvent: false });

      this.partnerForm.get('endereco')?.setErrors(null);
      this.partnerForm.get('bairro')?.setErrors(null);
      this.partnerForm.get('cidade')?.setErrors(null);
      this.partnerForm.get('estado')?.setErrors(null);
      this.partnerForm.get('cep')?.setErrors(null);
    }
  }
}

